// src/forms/data_hub_init.js
// Init-flow helpers split out of data_hub.js to keep the class file focused on
// field initialization. Two responsibilities live here:
//   1. Building the deferred `user_data_loaded_promise` and its post-resolve chain.
//   2. Orchestrating `start_data_hub_init` — parallel organization_metadata + user_jwt
//      fetches, prefill, and the first dispatch of `data-hub-init`.
import defaultsDeep from 'lodash.defaultsdeep';

import { inject_styles } from '../helpers/index';
import { default_theme } from '../helpers/static_vars';
import { load_user_data_from_jwt_url_params } from '../helpers/get_process_user_data';
import { merge_user_data_browser } from '../helpers/user_data_smart_merge';
import { normalize_data_hub_organization_id_string } from '../helpers/action_request_helpers';
import { updateActblueConfigWithDatahub } from '../helpers/actblue_integration';
import { bind_all_tel_inputs_intl } from '../tel/telephone_sms_inputs';
import {
    add_form_input_loading_state,
    remove_form_input_loading_state,
    add_form_loading_overlay,
    remove_form_loading_overlay,
    prefill_form
} from './shared_form_ux_functions';
import {
    load_organization_public_metadata,
    organization_metadata_is_complete_enough_to_skip_fetch
} from './data_hub_metadata';

/**
 * Assigns `user_data_loaded_promise` and its resolve/reject hooks to the instance.
 * The promise chain merges window.data_hub_user_data, fires `data-hub-new-user-data`,
 * binds tel inputs when enabled, and refreshes the ActBlue config.
 *
 * @param {Object} data_hub_instance - DataHub instance.
 * @returns {void}
 */
export function setup_user_data_loaded_promise(data_hub_instance) {
    data_hub_instance.user_data_loaded = false;
    data_hub_instance.user_data_loaded_promise = new Promise((resolve, reject) => {
        data_hub_instance.resolve_user_data_loaded = () => {
            data_hub_instance.user_data_loaded = true;
            return resolve();
        };
        data_hub_instance.reject_user_data_loaded = (error) => {
            data_hub_instance.user_data_loaded = false;
            return reject(error);
        };
    }).then(async () => {
        data_hub_instance.user_data = await merge_user_data_browser(
            window.data_hub_user_data || {},
            data_hub_instance.user_data || {}
        );

        updateActblueConfigWithDatahub(data_hub_instance);

        document.dispatchEvent(new CustomEvent('data-hub-new-user-data', {
            detail: {
                user_data: data_hub_instance.user_data || {},
                prefill_input_selector: data_hub_instance.prefill_input_selector || null,
                data_hub_instance,
                target: data_hub_instance.target || document,
                source: 'init'
            },
            bubbles: true,
            composed: true
        }));

        if (data_hub_instance.bind_tel_inputs_intl) {
            data_hub_instance.iti_inputs = await bind_all_tel_inputs_intl(
                data_hub_instance.target || document,
                data_hub_instance
            );
        }
    }).catch((error) => {
        data_hub_instance.error_function?.('Error in user_data_loaded_promise: ' + error, null);
    });
}

/**
 * Orchestrates the async portion of DataHub init: parallel organization_metadata and
 * user_jwt fetches, theme re-application, and form prefill. Resolves
 * `user_data_loaded_promise` and dispatches `data-hub-init` at the end.
 *
 * @param {Object} data_hub_instance - DataHub instance.
 * @returns {Promise<void>}
 */
export async function start_data_hub_init(data_hub_instance) {
    if (data_hub_instance.prefill_all_forms) {
        add_form_input_loading_state(
            data_hub_instance.target || document,
            data_hub_instance.prefill_input_selector
        );
        add_form_loading_overlay(data_hub_instance.target || document);
    }

    // Safety net: if user_data takes too long, drop loading state so the form
    // is usable. prefill_form() still applies when data arrives — fill_element_with_data
    // skips inputs the user has already typed into (data-user-modified).
    if (data_hub_instance.prefill_timeout) {
        setTimeout(() => {
            data_hub_instance.has_hit_prefill_timeout = true;
            remove_form_input_loading_state(
                data_hub_instance.target || document,
                data_hub_instance.prefill_input_selector
            );
            remove_form_loading_overlay(data_hub_instance.target || document);
        }, data_hub_instance.prefill_timeout);
    }

    const skip_organization_metadata_network_fetch = organization_metadata_is_complete_enough_to_skip_fetch(
        data_hub_instance.organization_metadata
    );
    const organization_metadata_load_promise = skip_organization_metadata_network_fetch
        ? Promise.resolve(data_hub_instance.organization_metadata)
        : load_organization_public_metadata(data_hub_instance);

    // user_jwt only reads forms_domain, organization_id, url_params, and user_data — all
    // already set by the constructor. Fire in parallel with organization_metadata.
    const user_data_promise = load_user_data_from_jwt_url_params(data_hub_instance);

    await organization_metadata_load_promise.then((metadata) => {
        if (metadata && typeof metadata === 'object') {
            data_hub_instance.organization_metadata = metadata;
        } else if (metadata) {
            try {
                data_hub_instance.organization_metadata = JSON.parse(metadata);
            } catch (error) {
                data_hub_instance.error_function?.('Error parsing organization metadata: ' + error, null);
                data_hub_instance.organization_metadata = {};
            }
        }
        if (!data_hub_instance.organization_id && data_hub_instance.organization_metadata?.organization_id) {
            data_hub_instance.organization_id = normalize_data_hub_organization_id_string(
                data_hub_instance.organization_metadata.organization_id
            );
        }
        if (data_hub_instance.organization_metadata?.actblue_default_token) {
            data_hub_instance.actblue_token = data_hub_instance.organization_metadata.actblue_default_token;
        }
        if (data_hub_instance.organization_metadata?.actblue_default_page_url) {
            data_hub_instance.actblue_default_page_url = data_hub_instance.organization_metadata.actblue_default_page_url;
        }

        // Priority: organization_metadata body -> forms_domain set in constructor -> hard fail
        data_hub_instance.forms_domain = data_hub_instance.organization_metadata?.data_hub_action_domain
            || data_hub_instance.organization_metadata?.forms_domain
            || data_hub_instance.forms_domain;
        if (!data_hub_instance.forms_domain) {
            data_hub_instance.error_function?.(
                'No data_hub_action_domain resolved — cannot reach the API.',
                'Configuration error. Please contact support.'
            );
            return;
        }

        if (typeof window !== 'undefined') {
            window.data_hub_organization_metadata = data_hub_instance.organization_metadata;
        }

        // Re-inject theme if organization_metadata fetched async brought a theme_json.
        // inject_styles is hash-keyed and updates in place — no-op when unchanged (e.g. server-injected
        // metadata already matches). Priority: config_theme > organization_metadata.theme_json > default.
        if (data_hub_instance.style_target && data_hub_instance.organization_metadata?.theme_json) {
            data_hub_instance.theme = defaultsDeep(
                {},
                data_hub_instance.config_theme || {},
                data_hub_instance.organization_metadata.theme_json,
                default_theme
            );
            inject_styles(data_hub_instance.theme, data_hub_instance.style_target);
        }
    });

    try {
        await user_data_promise;

        remove_form_input_loading_state(
            data_hub_instance.target || document,
            data_hub_instance.prefill_input_selector
        );
        remove_form_loading_overlay(data_hub_instance.target || document);
        if (data_hub_instance.prefill_all_forms) {
            const prefill_root = data_hub_instance.target || document;
            prefill_form(
                prefill_root,
                data_hub_instance.user_data,
                data_hub_instance.prefill_input_selector,
                data_hub_instance.reset_button_selector,
                null,
                false,
                data_hub_instance
            );
        }
        data_hub_instance.resolve_user_data_loaded();
        data_hub_instance.callDataHubDispatchEventThenCallbackInit();
    } catch (error) {
        remove_form_input_loading_state(
            data_hub_instance.target || document,
            data_hub_instance.prefill_input_selector
        );
        remove_form_loading_overlay(data_hub_instance.target || document);
        data_hub_instance.reject_user_data_loaded(error);
        data_hub_instance.callDataHubDispatchEventThenCallbackInit();
    }
}
